// Centralized CSRF handling for axios requests.
//
// Django's CsrfViewMiddleware expects the token from the `csrftoken` cookie
// to be echoed back in the `X-CSRFToken` header on unsafe methods.
// attachCsrfInterceptor() adds it once, for any axios instance.
//
// The SPA and API live on different hosts, so document.cookie usually cannot
// see the API's csrftoken cookie. In that case the token is fetched from
// /api/csrf_token/ (which also sets the cookie) and cached. Login rotates the
// token, so a CSRF 403 clears the cache and retries once.

import axios from 'axios';

const UNSAFE_METHODS = ['post', 'put', 'patch', 'delete'];
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/';

let cachedToken = null;
let pending = null;

export function getCsrfToken() {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

async function fetchCsrfToken(baseURL) {
  if (!pending) {
    const base = (baseURL || API_BASE).replace(/\/+$/, '');
    pending = axios
      .get(`${base}/api/csrf_token/`, { withCredentials: true })
      .then((res) => {
        cachedToken = res.data && res.data.csrfToken;
        return cachedToken;
      })
      .catch(() => null)
      .finally(() => {
        pending = null;
      });
  }
  return pending;
}

export function attachCsrfInterceptor(axiosInstance) {
  axiosInstance.interceptors.request.use(async (config) => {
    const method = (config.method || 'get').toLowerCase();
    if (UNSAFE_METHODS.includes(method)) {
      config.headers = config.headers || {};
      if (!config.headers['X-CSRFToken']) {
        const token =
          cachedToken || getCsrfToken() || (await fetchCsrfToken(config.baseURL));
        if (token) config.headers['X-CSRFToken'] = token;
      }
    }
    return config;
  });

  axiosInstance.interceptors.response.use(undefined, async (error) => {
    const { response, config } = error;
    const isCsrfFailure =
      response &&
      response.status === 403 &&
      typeof response.data?.message === 'string' &&
      response.data.message.startsWith('CSRF verification failed');
    if (isCsrfFailure && config && !config._csrfRetried) {
      config._csrfRetried = true;
      cachedToken = null;
      const token = await fetchCsrfToken(config.baseURL);
      if (token) {
        config.headers = { ...(config.headers || {}), 'X-CSRFToken': token };
        return axiosInstance.request(config);
      }
    }
    return Promise.reject(error);
  });
  return axiosInstance;
}
